import { FiBook } from 'react-icons/fi';

export const awlSublist9Data = {
  courseId: 'awl-sublist-9',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 9',
  icon: FiBook,
  overview: {
    impact: 'Name subtle academic relationships — what is integral, what you anticipate, what stays coherent',
    focus: 'anticipate, coherent, integral — plus diminish, inherent, qualitative, trigger',
    uses: 'Writing Task 2 structure (coherent argument) and Reading on social change, ethics, and planning',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word meanings and usage',
      content: `<p>Sublist 9 is about relationships between ideas and parts of a system. <em>Integral</em> means a part is necessary to the whole. <em>Coherent</em> means the parts fit together logically — the same word IELTS uses in Coherence and Cohesion. <em>Anticipate</em> means see a problem coming, which is why good writers anticipate the reader’s objections.</p>
<p>The full Sublist 9 headword set in this course is: accommodate, analogy, anticipate, assure, attain, behalf, bulk, cease, coherent, coincide, commence, incompatible, concurrent, confine, controversy, conversely, devote, diminish, distort, duration, erode, ethic, format, inherent, insight, integral, intermediate, mature, mediate, minimal, mutual, overlap, passive, portion, preliminary, protocol, qualitative, refine, restrain, revolution, rigid, route, scenario, sphere, supplement, suspend, temporary, trigger, unify, violate, vision.</p>
<p>These words are gold for nuance. <em>Inherent</em> and <em>integral</em> are not twins: inherent means built into the nature of something; integral means it is a necessary component. A risk can be inherent in a job; training can be integral to safety. <em>Qualitative</em> describes kind, not amount — opposite in research talk to quantitative (not on this list, but often paired).</p>`,
      points: [
        'Coherent is an essay-quality word: a coherent argument is easy to follow.',
        'Anticipate + objection is a high-band discussion move.',
        'Trigger, diminish, erode, and cease describe change without informal “start / go down / stop.”',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['anticipate', 'To expect and prepare for something before it happens', 'Good writers anticipate the reader’s objections.'],
          ['cease', 'To come or bring to an end', 'Night buses should not cease simply because daytime demand is higher.'],
          ['coherent', 'Logical and consistent; forming a unified whole', 'The essay must present a coherent argument supported by evidence.'],
          ['controversy', 'Prolonged public disagreement', 'There is ongoing controversy about the role of homework.'],
          ['diminish', 'To make or become smaller or less', 'If wages diminish in real terms, staff will leave.'],
          ['inherent', 'Existing as a natural or permanent part of something', 'There is an inherent tension between privacy and security.'],
          ['insight', 'A clear, deep understanding', 'Interviews gave researchers insight into why students drop out.'],
          ['integral', 'Necessary to make a whole complete', 'Feedback is an integral part of any mature assessment system.'],
          ['mutual', 'Experienced or done by two or more parties towards each other', 'Trade works when the benefits are mutual.'],
          ['qualitative', 'Relating to quality or kind rather than quantity', 'Qualitative interviews explained the pattern the numbers only hinted at.'],
          ['scenario', 'A possible sequence of events', 'In the worst-case scenario, the clinic would close within a year.'],
          ['trigger', 'To cause something to start; the cause itself', 'A single fee increase can trigger a sharp fall in applications.'],
        ],
      },
    },
    {
      title: 'Word families',
      content: `<p>Several Sublist 9 adjectives are more common than their verbs. <em>Coherent</em> → <em>coherence</em> / <em>incoherent</em>. <em>Integral</em> has no everyday verb; use “form an integral part of.” <em>Anticipate</em> → <em>anticipation</em> / <em>anticipatory</em>. After “will” you need <em>anticipate</em>, not <em>anticipation</em>.</p>
<p>Linkers on this list do grammatical work. <em>Conversely</em> introduces the opposite picture. <em>On behalf of</em> is a fixed phrase (not “in behalf”). <em>Incompatible with</em> is the pairing, not “incompatible to.” <em>Ethic</em> → <em>ethical</em> / <em>ethics</em> is essential for environment and technology essays.</p>`,
      points: [
        'Anticipate / anticipation / anticipatory — verb after will; noun after the.',
        'Coherent / coherence / coherently — IELTS writing criterion language.',
        'Trigger is noun and verb: a trigger / to trigger a change.',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['anticipate', 'anticipation', 'anticipate', 'anticipatory'],
          ['cease', 'cessation', 'cease', 'ceaseless, ceaselessly'],
          ['coherent', 'coherence', '—', 'coherent, coherently, incoherent'],
          ['controversy', 'controversy', '—', 'controversial, controversially'],
          ['diminish', 'diminution', 'diminish', 'diminished, diminishing'],
          ['inherent', '—', '—', 'inherent, inherently'],
          ['insight', 'insight', '—', 'insightful'],
          ['integral', '—', '—', 'integral, integrally'],
          ['mutual', '—', '—', 'mutual, mutually'],
          ['qualitative', '—', '—', 'qualitative, qualitatively'],
          ['trigger', 'trigger', 'trigger', 'triggered'],
          ['unify', 'unity, unification', 'unify', 'unified'],
        ],
      },
    },
    {
      title: 'Common collocations',
      content: `<p>These partnerships make Sublist 9 usable under time pressure. “Anticipate objections,” “a coherent argument,” “an integral part of,” “inherent risk,” “qualitative data,” and “trigger a change” are ready-made. “Do an anticipate” is not.</p>
<p>Planning language: <em>preliminary results</em>, <em>commence a project</em>, <em>a temporary measure</em>, <em>suspend a service</em>, <em>protocol for</em>. Relationship language: <em>mutual respect</em>, <em>overlap between</em>, <em>incompatible with</em>, <em>on behalf of</em>. Change language: <em>erode trust</em>, <em>refine a method</em>, <em>a rigid system</em>, <em>a revolution in</em>.</p>`,
      points: [
        'Integral almost always appears as “an integral part of…”',
        'Anticipate usually takes a noun: objections, demand, problems — not a that-clause in the most formal style, though “anticipate that” is also used.',
      ],
      examples: [
        { label: 'anticipate', text: 'anticipate objections, anticipate demand, widely anticipated' },
        { label: 'coherent', text: 'coherent argument, coherent strategy, lack of coherence' },
        { label: 'integral', text: 'an integral part of, integral to, integrally linked' },
        { label: 'inherent', text: 'inherent risk, inherent in, inherently unfair' },
        { label: 'diminish', text: 'diminish over time, diminish the impact, diminishing returns' },
        { label: 'trigger', text: 'trigger a decline, trigger a debate, the trigger for' },
        { label: 'qualitative', text: 'qualitative research, qualitative difference, qualitative evidence' },
        { label: 'scenario', text: 'worst-case scenario, likely scenario, scenario planning' },
        { label: 'controversy', text: 'cause controversy, at the centre of controversy, controversial plan' },
        { label: 'mutual', text: 'mutual benefit, mutual respect, mutually exclusive' },
      ],
    },
    {
      title: 'Using Sublist 9 in IELTS writing',
      content: `<p>Open a body paragraph with coherence language: “A coherent transport policy cannot treat cycling as optional; it has to be integral to street design.” That sentence uses two target words and sounds like an argument, not a word list. In conclusions, <em>on balance</em> (from speaking lessons) plus <em>scenario</em> works: “Even in a cautious scenario, the benefits do not diminish.”</p>
<p>Reading will paraphrase <em>cease</em> as stop, <em>commence</em> as begin, <em>confine</em> as limit, and <em>distort</em> as twist. If you only know the informal partner, you will still find the answer — but slowly. Learn both directions.</p>`,
      points: [
        'Use anticipate when you mention the other side before the examiner does.',
        'Use qualitative when numbers alone cannot explain why people act.',
        'Rigid and flexible (Sublist 6) make a clean contrast pair.',
      ],
      examples: [
        { label: 'Weak sentence', text: 'Good writers think about what the reader will say against them.' },
        { label: 'Strong sentence', text: 'Good writers anticipate the reader’s objections.' },
        { label: 'Weak sentence', text: 'Feedback is a necessary piece of a grown-up exam system.' },
        { label: 'Strong sentence', text: 'Feedback is an integral part of any mature assessment system.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match word to definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        {
          text: 'What does “anticipate” mean?',
          options: ['To expect and prepare for something before it happens', 'To forget a problem on purpose', 'To copy another writer’s conclusion'],
          answer: 'To expect and prepare for something before it happens',
          explanation: 'Anticipate is forward-looking. Writers who anticipate objections sound controlled rather than surprised.',
        },
        {
          text: 'What does “coherent” mean?',
          options: ['Logical and consistent as a whole', 'Loud and emotional', 'Short and unfinished'],
          answer: 'Logical and consistent as a whole',
          explanation: 'A coherent argument holds together. IELTS uses the related noun coherence as an official writing criterion.',
        },
        {
          text: 'What does “integral” mean?',
          options: ['Necessary to make a whole complete', 'Optional and decorative', 'Only used in mathematics classes'],
          answer: 'Necessary to make a whole complete',
          explanation: 'Integral means the part cannot be removed without breaking the whole. It is stronger than “quite useful.”',
        },
        {
          text: 'What does “inherent” mean?',
          options: ['Existing as a natural or permanent part of something', 'Added later as an extra option', 'Hired from outside the organisation'],
          answer: 'Existing as a natural or permanent part of something',
          explanation: 'Inherent describes what is built in. An inherent risk is not an accident; it belongs to the activity itself.',
        },
        {
          text: 'What does “trigger” mean as a verb?',
          options: ['To cause something to start', 'To measure something slowly', 'To translate something'],
          answer: 'To cause something to start',
          explanation: 'Trigger means set off. A fee increase can trigger a fall in applications. It is more sudden than “encourage.”',
        },
      ],
    },
    {
      title: 'Complete the sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct Sublist 9 word.',
      questions: [
        {
          text: 'Good writers ____ the reader’s objections.',
          answer: 'anticipate',
          explanation: 'Anticipate is the verb that means expect and prepare for. After a plural subject in the present simple, use anticipate.',
        },
        {
          text: 'The essay must present a ____ argument, not a list of unconnected points.',
          answer: 'coherent',
          explanation: 'Coherent describes an argument whose parts fit. It is the adjective IELTS writing teachers use constantly.',
        },
        {
          text: 'Practical training is an ____ part of medical education.',
          answer: 'integral',
          explanation: '“An integral part of” is the fixed collocation. Integral means the training is necessary to the whole.',
        },
        {
          text: 'A sudden interest-rate rise can ____ a fall in house prices.',
          answer: 'trigger',
          explanation: 'Trigger means cause to start, and it collocates naturally with a fall, a crisis, or a debate.',
        },
      ],
    },
    {
      title: 'Choose the correct word form',
      type: 'select',
      instructions: 'Select the grammatically correct form for each sentence.',
      questions: [
        {
          text: 'Good writers ____ the reader’s objections. (anticipate)',
          options: ['anticipate', 'anticipation', 'anticipatory'],
          answer: 'anticipate',
          explanation: 'The clause needs a present plural verb. Anticipation is a noun; anticipatory is an adjective.',
        },
        {
          text: 'The paragraph lacks ____; the ideas jump without links. (coherent)',
          options: ['coherent', 'coherence', 'coherently'],
          answer: 'coherence',
          explanation: 'After “lacks” you need a noun. Coherence is the name of the quality; coherent would need a noun after it.',
        },
        {
          text: 'Privacy risks are ____ in large data systems. (inherent)',
          options: ['inherent', 'inherently', 'inherit'],
          answer: 'inherent',
          explanation: '“Are inherent in” is the adjective + in pairing. Inherit is a different verb meaning receive from a parent.',
        },
        {
          text: 'The two studies offer ____ different stories, not just different numbers. (qualitative)',
          options: ['qualitative', 'qualitatively', 'quality'],
          answer: 'qualitatively',
          explanation: 'You need an adverb to modify “different.” Qualitatively different is a standard research phrase.',
        },
      ],
    },
  ],
  tips: [
    'Anticipate objections in Task 2 before you rebut them — that is nuance plus vocabulary.',
    'Keep inherent (built-in nature) distinct from integral (necessary part).',
    'Use coherent about arguments and plans, not about a single factual number.',
    'On behalf of is the phrase; “in behalf of” is not standard in IELTS academic writing.',
  ],
  dailyPractice: [
    'Write one topic sentence that uses integral and one that uses inherent — then check they are not interchangeable.',
    'Take a Band 6 paragraph and rewrite the opening so the argument is more coherent.',
    'Practise “anticipate + noun”: objections, demand, delays, criticism.',
    'Read a comment piece and note controversy, scenario, or trigger if they appear.',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 9 vocabulary course covering Coxhead headwords such as anticipate, cease, coherent, controversy, diminish, inherent, insight, integral, mutual, qualitative, scenario, and trigger. Includes the full stub list, word families, collocations, and IELTS uses for relationships, planning, and coherent argument.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist9Data,
      title: awlSublist9Data.title + ' (Foundations)',
      overview: { ...awlSublist9Data.overview, duration: '15-20 minutes' },
      sections: awlSublist9Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist9Data.sections.length / 2))),
      exercises: awlSublist9Data.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist9Data,
      title: awlSublist9Data.title + ' (Advanced)',
      overview: { ...awlSublist9Data.overview, duration: '30-35 minutes' },
      sections: awlSublist9Data.sections,
      exercises: awlSublist9Data.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist9Data.tips || []),
        'For Band 8+: pair conversely with a concrete opposite scenario, not a vague “on the other hand.”',
        'Advanced tip: use qualitative insight to explain a quantitative trend in one paragraph.',
      ],
    };
  }
  return awlSublist9Data;
}
